import { Search, X } from 'lucide-react'
import { useState } from 'react'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
] as const

export type EmailFilterValues = {
  status: string
  account: string
  domain: string
  from: string
  to: string
  search: string
}

const EMPTY_FILTERS: EmailFilterValues = {
  status: '',
  account: '',
  domain: '',
  from: '',
  to: '',
  search: '',
}

interface EmailFiltersProps {
  values: EmailFilterValues
  onApply: (filters: EmailFilterValues) => void
}

export function EmailFilters({ values, onApply }: EmailFiltersProps): React.JSX.Element {
  const [draft, setDraft] = useState<EmailFilterValues>(values)

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    onApply(draft)
  }

  function handleClear(): void {
    setDraft(EMPTY_FILTERS)
    onApply(EMPTY_FILTERS)
  }

  const hasFilters = Object.values(draft).some((v) => v !== '')

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-surface-3 bg-surface-0 p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Recipient / subject search */}
        <div className="xl:col-span-2">
          <label
            htmlFor="email-search"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-text-tertiary" />
            <input
              id="email-search"
              type="text"
              placeholder="Recipient or subject..."
              value={draft.search}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
              className="w-full rounded-lg border border-surface-3 bg-surface-0 py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
            />
          </div>
        </div>

        {/* Status dropdown */}
        <div>
          <label
            htmlFor="email-status"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Status
          </label>
          <select
            id="email-status"
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Account search */}
        <div>
          <label
            htmlFor="email-account"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Account
          </label>
          <input
            id="email-account"
            type="text"
            placeholder="Account email..."
            value={draft.account}
            onChange={(e) => setDraft({ ...draft, account: e.target.value })}
            className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
          />
        </div>

        {/* Domain */}
        <div>
          <label
            htmlFor="email-domain"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Domain
          </label>
          <input
            id="email-domain"
            type="text"
            placeholder="e.g. gmail.com"
            value={draft.domain}
            onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
            className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
          />
        </div>

        {/* From address */}
        <div>
          <label
            htmlFor="email-from"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            From
          </label>
          <input
            id="email-from"
            type="text"
            placeholder="Sender address..."
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
          />
        </div>
      </div>

      {/* Actions row */}
      <div className="mt-3 flex items-center gap-3">
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-admin-600"
        >
          <Search className="h-3.5 w-3.5" />
          Search
        </button>
        {hasFilters && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
            Clear filters
          </button>
        )}
      </div>
    </form>
  )
}
